/**
 * Timeout de sesión por inactividad real (no desde el login).
 * Renueva el contador solo con interacción explícita del usuario.
 */

import { eventBus } from './EventBus';

const STORAGE_KEY = 'last_activity_timestamp';
const LEGACY_STORAGE_KEY = 'login_timestamp';
const TOUCH_THROTTLE_MS = 5_000;
const SCROLL_THROTTLE_MS = 30_000;

export type SessionInactivityConfig = {
  idleTimeoutMs: number;
  warningBeforeMs: number;
  onWarning: () => void;
  onTimeout: () => void;
};

let config: SessionInactivityConfig | null = null;
let lastActivityAt = Date.now();
let lastTouchHandledAt = 0;
let lastScrollHandledAt = 0;
let warningTimer: ReturnType<typeof setTimeout> | null = null;
let logoutTimer: ReturnType<typeof setTimeout> | null = null;
let domListenersAttached = false;
let eventBusUnsub: (() => void) | null = null;

function clearTimers(): void {
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
  if (logoutTimer) {
    clearTimeout(logoutTimer);
    logoutTimer = null;
  }
}

function persistActivity(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(lastActivityAt));
  } catch {
    // sessionStorage puede no estar disponible en algunos contextos
  }
}

function loadStoredActivity(): number | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = parseInt(stored, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

function reschedule(): void {
  if (!config) return;

  clearTimers();

  const elapsed = Date.now() - lastActivityAt;
  const remaining = config.idleTimeoutMs - elapsed;

  if (remaining <= 0) {
    config.onTimeout();
    return;
  }

  const warnIn = remaining - config.warningBeforeMs;
  if (warnIn > 0) {
    warningTimer = setTimeout(() => {
      config?.onWarning();
    }, warnIn);
  } else {
    config.onWarning();
  }

  logoutTimer = setTimeout(() => {
    config?.onTimeout();
  }, remaining);
}

export function touchSessionActivity(
  _source?: string,
  options?: { force?: boolean }
): void {
  if (!config) return;

  const now = Date.now();
  if (!options?.force && now - lastTouchHandledAt < TOUCH_THROTTLE_MS) {
    return;
  }

  lastTouchHandledAt = now;
  lastActivityAt = now;
  persistActivity();
  reschedule();
}

function handleDomActivity(event: Event): void {
  if (!config) return;

  if (event.type === 'scroll') {
    const now = Date.now();
    if (now - lastScrollHandledAt < SCROLL_THROTTLE_MS) return;
    lastScrollHandledAt = now;
  }

  touchSessionActivity(event.type);
}

function handleVisibilityChange(): void {
  if (!config || document.hidden) return;

  const stored = loadStoredActivity();
  if (stored !== null) {
    lastActivityAt = stored;
  }

  const elapsed = Date.now() - lastActivityAt;
  if (elapsed >= config.idleTimeoutMs) {
    config.onTimeout();
    return;
  }

  reschedule();
}

function attachDomListeners(): void {
  if (domListenersAttached || typeof document === 'undefined') return;

  const passiveCapture: AddEventListenerOptions = { capture: true, passive: true };
  document.addEventListener('click', handleDomActivity, passiveCapture);
  document.addEventListener('touchstart', handleDomActivity, passiveCapture);
  document.addEventListener('keydown', handleDomActivity, passiveCapture);
  document.addEventListener('scroll', handleDomActivity, passiveCapture);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  domListenersAttached = true;
}

function detachDomListeners(): void {
  if (!domListenersAttached || typeof document === 'undefined') return;

  document.removeEventListener('click', handleDomActivity, true);
  document.removeEventListener('touchstart', handleDomActivity, true);
  document.removeEventListener('keydown', handleDomActivity, true);
  document.removeEventListener('scroll', handleDomActivity, true);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  domListenersAttached = false;
}

export function startSessionInactivity(nextConfig: SessionInactivityConfig): void {
  stopSessionInactivity();

  config = nextConfig;
  lastTouchHandledAt = 0;
  lastScrollHandledAt = 0;

  const stored = loadStoredActivity();
  lastActivityAt = stored ?? Date.now();
  persistActivity();

  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignorar
  }

  attachDomListeners();
  eventBusUnsub = eventBus.on('session:activity', () => {
    touchSessionActivity('eventBus');
  });

  reschedule();
}

export function stopSessionInactivity(): void {
  clearTimers();
  detachDomListeners();

  if (eventBusUnsub) {
    eventBusUnsub();
    eventBusUnsub = null;
  }

  config = null;

  try {
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignorar
  }
}

export function isSessionInactivityActive(): boolean {
  return config !== null;
}
